package dev.youndie.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initTracyPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("tracy.md", FrontMatterElement.Builder { addScalar("title", "tracy"); addScalar("searchTitle", "Structured logging for Ktor with MCP — Kotlin/Native, SQLite, no Elasticsearch"); addScalar("description", "Logs for Ktor services shaped for a coding agent to read. One native binary and one SQLite file; MCP is the primary read path, not a bolt-on."); addScalar("library", "tracy"); addScalar("post", "/blog/logs-for-an-agent-to-read"); }))
}

@Page("/tracy")
@Layout(".components.layouts.LibraryLayout")
@Composable
fun TracyPage() {
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Between \"")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kubectl logs")
        }
        org.jetbrains.compose.web.dom.Text(" is enough\" and \"let's run Loki, Promtail and Grafana\" there is nothing. tracy is one self-contained binary and one SQLite file — no JVM, no Elasticsearch, no shipper — and its primary reader is a coding agent rather than a person with a browser.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-you-get") }) {
        org.jetbrains.compose.web.dom.Text("What you get")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("End-to-end traces.")
            }
            org.jetbrains.compose.web.dom.Text(" You have a ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("traceId")
            }
            org.jetbrains.compose.web.dom.Text(", you get the whole chain: a tree of spans with durations plus every log line of that request, across every service, in one call. Correlation travels as a standard W3C ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("traceparent")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Business entity lookup.")
            }
            org.jetbrains.compose.web.dom.Text(" Support never brings a ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("traceId")
            }
            org.jetbrains.compose.web.dom.Text("; they bring an ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("order_id")
            }
            org.jetbrains.compose.web.dom.Text(". Mark a field as an entity key and you get that entity's history across services and across unrelated traces.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Templates and data separated at write time")
            }
            org.jetbrains.compose.web.dom.Text(", so exact event frequencies are countable later instead of reverse-engineered out of rendered strings.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("FTS5 search")
            }
            org.jetbrains.compose.web.dom.Text(" with the trigram tokenizer, plus time-window queries across services.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Seven read-only MCP tools")
            }
            org.jetbrains.compose.web.dom.Text(" with a hard context budget, a two-phase content release and a static screen over untrusted text — log content is attacker-influenced data being handed to a model.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("install") }) {
        org.jetbrains.compose.web.dom.Text("Install")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Run the server:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-bash") }) { org.jetbrains.compose.web.dom.Text("""docker run -p 8080:8080 -e TRACY_INGEST_KEY=dev-key ghcr.io/youndie/tracy:latest
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Add the agent to a Ktor service:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""repositories { maven("https://reposilite.kotlin.website/snapshots") }
dependencies { implementation("io.github.youndie.tracy:agent:<VERSION>") }
""") } }
    org.jetbrains.compose.web.dom.H2(attrs = { id("where-it-actually-is") }) {
        org.jetbrains.compose.web.dom.Text("Where it actually is")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Running on a staging cluster with four services reporting into it, 293 tests, read over MCP daily. Those four services are ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("quiet")
        }
        org.jetbrains.compose.web.dom.Text(" — a day of them is on the order of a hundred kilobytes — so the volume figures are still arithmetic rather than measurements.")
    }
}
